import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
  ChevronLeft, Save, Download, Undo, Redo, 
  Type, Square, Circle, Image, MousePointer,
  Trash2, Layers, Plus, X,
  Sparkles, Send, Loader, BookOpen, PenTool, Lightbulb,
  Menu, Settings2, Check, Grid3X3, Table, QrCode, Puzzle, Pin, GripVertical,
  FileText, Mic, Volume2, Play, StopCircle, CheckCircle, XCircle,
  RotateCcw, Users, Printer, Eye, Library, Bold, Italic, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, Copy, Scissors, Clipboard, Maximize2,
  Target, GraduationCap,
  // Phase B icons
  Highlighter, Pencil, Triangle, Star, ArrowRight, Palette,
  // Phase D icons - already have Layers, FileText
  Droplet, Stamp, Hash, LayoutGrid
} from "lucide-react";
import { Stage, Layer, Rect, Text, Circle as KonvaCircle, Image as KonvaImage, Line, Group, Transformer, Arrow as KonvaArrow, RegularPolygon, Star as KonvaStar } from "react-konva";
import { v4 as uuidv4 } from "uuid";
import QRCodeLib from "qrcode";
import { ResizableElement, TextResizable, CircleResizable } from "../components/editor/ResizableElement";
import { NIVEAUX_SCOLAIRES } from "../context/AuthContext";
import { 
  PAGE_FORMATS, 
  ORIENTATIONS, 
  getPageDimensions, 
  getScaledDimensions 
} from "../components/editor/pageFormats";
import { PageSettingsDialog, PageFormatIndicator } from "../components/editor/PageSettingsDialog";
import { ExportOutputDialog } from "../components/editor/ExportOutputDialog";
import { ExportPreviewDialog } from "../components/editor/ExportPreviewDialog";
import { MultiPageManager } from "../components/editor/MultiPageManager";
import { TargetAssignmentPanel, TargetIndicator } from "../components/editor/TargetAssignmentPanel";
import { PedagogicalTextKonva } from "../components/editor/PedagogicalTextElement";
import { ClipArtLibraryDialog } from "../components/editor/ClipArtLibraryDialog";
import { ElementContextMenu } from "../components/editor/ElementContextMenu";
import { ResourceMetadataSheet } from "../components/editor/ResourceMetadataSheet";
import { LayersPanel } from "../components/editor/LayersPanel";
import { FloatingToolbar } from "../components/editor/FloatingToolbar";
import { LibraryPanel } from "../components/editor/LibraryPanel";
import { AudioRecorderPanel } from "../components/editor/AudioRecorderPanel";
import { AdvancedToolsDialog } from "../components/editor/AdvancedToolsDialog";
import { PDFComposerDialog } from "../components/editor/PDFComposerDialog";
import { AIAssistantPanel } from "../components/editor/AIAssistantPanel";
import { BottomSheet, AudioPreviewButton, AudioRecorder } from "../components/editor";
import { PropertiesContent } from "../components/editor/PropertiesContent";
import { generateResourceVariants, getResource, getSession } from "../lib/api";
import { toast } from "sonner";

// Element types
const ELEMENT_TYPES = {
  TEXT: "text",
  PEDAGOGICAL_TEXT: "pedagogical_text",
  RECT: "rect",
  CIRCLE: "circle",
  IMAGE: "image",
  SEYES: "seyes",        // Lignes Seyes
  GRID: "grid",          // Quadrillage
  TABLE: "table",        // Tableau
  QRCODE: "qrcode",      // QR Code
  AUDIO: "audio",        // Audio element (recording or TTS)
  QCM_INTERACTIVE: "qcm_interactive", // Interactive QCM with feedback
  // New Phase B elements
  HIGHLIGHTER: "highlighter",  // Surligneur
  FREEDRAW: "freedraw",        // Écriture libre / dessin
  TRIANGLE: "triangle",        // Triangle
  STAR: "star",                // Étoile
  ARROW: "arrow",              // Flèche
};

// Polices disponibles (script et cursive en priorité)
const FONT_OPTIONS = [
  // Cursive / handwriting fonts for early grades
  { value: "Cursive", label: "Cursive", preview: "Aa", category: "cursive" },
  { value: "'Comic Sans MS', cursive", label: "Comic Sans", preview: "Aa", category: "cursive" },
  { value: "'Segoe Script', cursive", label: "Segoe Script", preview: "Aa", category: "cursive" },
  { value: "'Bradley Hand', cursive", label: "Bradley Hand", preview: "Aa", category: "cursive" },
  // Friendly sans-serif for reading
  { value: "Fredoka", label: "Fredoka", preview: "Aa", category: "display" },
  { value: "Nunito", label: "Nunito", preview: "Aa", category: "sans" },
  { value: "'Lexend', sans-serif", label: "Lexend (dyslexie)", preview: "Aa", category: "accessibility" },
  { value: "'OpenDyslexic', sans-serif", label: "OpenDyslexic", preview: "Aa", category: "accessibility" },
  // Classic fonts
  { value: "Georgia, serif", label: "Georgia", preview: "Aa", category: "serif" },
  { value: "Arial, sans-serif", label: "Arial", preview: "Aa", category: "sans" },
  { value: "'Times New Roman', serif", label: "Times New Roman", preview: "Aa", category: "serif" },
  // Monospace for code/math
  { value: "'Courier New', monospace", label: "Courier (code)", preview: "Aa", category: "mono" },
];

// Default element properties
const DEFAULT_PROPS = {
  [ELEMENT_TYPES.TEXT]: {
    text: "Appuyer pour editer",
    fontSize: 24,
    fontFamily: "Nunito",
    fontWeight: "normal",     // normal, bold
    fontStyle: "normal",      // normal, italic
    textDecoration: "none",   // none, underline, line-through
    fill: "#1e293b",
    stroke: "",               // Bordure du texte
    strokeWidth: 0,
    align: "left",            // left, center, right
    width: 200,
    lineHeight: 1.4,
    letterSpacing: 0,
    // Aides à la lecture (intégrées)
    showColoredSyllables: false,
    showSilentLetters: false,
    showLineAlternation: false,
    backgroundColor: "",      // Fond du texte
  },
  [ELEMENT_TYPES.RECT]: {
    width: 120,
    height: 80,
    fill: "#3b82f6",
    stroke: "#1d4ed8",
    strokeWidth: 2,
    cornerRadius: 8,
  },
  [ELEMENT_TYPES.CIRCLE]: {
    radius: 40,
    fill: "#f97316",
    stroke: "#ea580c",
    strokeWidth: 2,
  },
  [ELEMENT_TYPES.SEYES]: {
    width: 200,
    height: 120,
    lineColor: "#c4b5fd",
    marginColor: "#f87171",
    lineHeight: 8, // 8mm standard Seyes
    showMargin: true,
    // Texte intégré
    text: "",
    textFontSize: 20,
    textFontFamily: "Cursive",
    textColor: "#1e293b",
  },
  [ELEMENT_TYPES.GRID]: {
    width: 160,
    height: 160,
    cellSize: 20,
    strokeColor: "#94a3b8",
    strokeWidth: 1,
    gridType: "square", // square, dots, isometric
    // Texte intégré
    text: "",
    textFontSize: 16,
    textFontFamily: "Nunito",
    textColor: "#1e293b",
  },
  [ELEMENT_TYPES.TABLE]: {
    width: 240,
    height: 120,
    rows: 3,
    cols: 3,
    strokeColor: "#1e293b",
    strokeWidth: 2,
    fillColor: "#ffffff",
    headerColor: "#dbeafe",
  },
  [ELEMENT_TYPES.QRCODE]: {
    width: 100,
    height: 100,
    qrData: "https://example.com",
    bgColor: "#ffffff",
    fgColor: "#000000",
  },
  [ELEMENT_TYPES.AUDIO]: {
    width: 180,
    height: 60,
    audioType: "tts", // "tts" or "recording"
    text: "Bonjour !",  // For TTS
    audioData: null,    // Base64 audio for recording
    bgColor: "#8b5cf6",
  },
  [ELEMENT_TYPES.QCM_INTERACTIVE]: {
    width: 280,
    height: 200,
    question: "Question ?",
    options: ["Reponse A", "Reponse B", "Reponse C"],
    correctIndex: 0,
    showFeedback: true,
    feedbackSound: true,
  },
  // New Phase B elements
  [ELEMENT_TYPES.HIGHLIGHTER]: {
    width: 150,
    height: 30,
    fill: "#fef08a",       // Yellow highlight by default
    opacity: 0.6,
    cornerRadius: 4,
  },
  [ELEMENT_TYPES.FREEDRAW]: {
    points: [],            // Array of {x, y} coordinates
    stroke: "#1e293b",
    strokeWidth: 3,
    tension: 0.5,
    lineCap: "round",
    lineJoin: "round",
    width: 200,
    height: 200,
  },
  [ELEMENT_TYPES.TRIANGLE]: {
    width: 100,
    height: 100,
    fill: "#3b82f6",
    stroke: "#1e40af",
    strokeWidth: 2,
  },
  [ELEMENT_TYPES.STAR]: {
    width: 100,
    height: 100,
    fill: "#eab308",
    stroke: "#a16207",
    strokeWidth: 2,
    numPoints: 5,
    innerRadius: 20,
    outerRadius: 50,
  },
  [ELEMENT_TYPES.ARROW]: {
    points: [0, 0, 100, 0],  // Start and end points
    stroke: "#1e293b",
    strokeWidth: 3,
    fill: "#1e293b",
    pointerLength: 15,
    pointerWidth: 12,
    width: 100,
    height: 50,
  },
};

// Color palette - Extended with more educational-friendly colors
const COLOR_PALETTE = [
  // Primary row
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", 
  "#8b5cf6", "#ec4899", "#1e293b", "#64748b", "#ffffff",
  // Secondary row - softer tones
  "#fca5a5", "#fdba74", "#fde047", "#86efac", "#93c5fd",
  "#c4b5fd", "#f9a8d4", "#475569", "#94a3b8", "#f1f5f9",
  // Tertiary row - deeper tones
  "#991b1b", "#c2410c", "#a16207", "#15803d", "#1d4ed8",
  "#6d28d9", "#be185d", "#0f172a", "#334155", "#e2e8f0",
];

// Templates
const TEMPLATES = [
  { 
    id: "flashcard", 
    name: "Flashcard", 
    icon: "🃏",
    elements: [
      { type: ELEMENT_TYPES.RECT, x: 50, y: 50, width: 250, height: 150, fill: "#ffffff", stroke: "#3b82f6", strokeWidth: 3, cornerRadius: 16 },
      { type: ELEMENT_TYPES.TEXT, x: 80, y: 100, text: "Question ?", fontSize: 24, fill: "#1e293b", fontFamily: "Fredoka" },
    ]
  },
  { 
    id: "quiz", 
    name: "Quiz", 
    icon: "❓",
    elements: [
      { type: ELEMENT_TYPES.TEXT, x: 30, y: 30, text: "Question ?", fontSize: 22, fill: "#1e293b", fontFamily: "Fredoka", width: 280 },
      { type: ELEMENT_TYPES.RECT, x: 30, y: 90, width: 280, height: 45, fill: "#dbeafe", stroke: "#3b82f6", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.RECT, x: 30, y: 145, width: 280, height: 45, fill: "#dbeafe", stroke: "#3b82f6", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.RECT, x: 30, y: 200, width: 280, height: 45, fill: "#dbeafe", stroke: "#3b82f6", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.TEXT, x: 45, y: 100, text: "A. Reponse 1", fontSize: 16, fill: "#1e40af" },
      { type: ELEMENT_TYPES.TEXT, x: 45, y: 155, text: "B. Reponse 2", fontSize: 16, fill: "#1e40af" },
      { type: ELEMENT_TYPES.TEXT, x: 45, y: 210, text: "C. Reponse 3", fontSize: 16, fill: "#1e40af" },
    ]
  },
  { 
    id: "etiquette", 
    name: "Etiquettes", 
    icon: "🏷️",
    elements: [
      { type: ELEMENT_TYPES.RECT, x: 20, y: 100, width: 90, height: 50, fill: "#fef3c7", stroke: "#f59e0b", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.RECT, x: 125, y: 100, width: 90, height: 50, fill: "#dbeafe", stroke: "#3b82f6", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.RECT, x: 230, y: 100, width: 90, height: 50, fill: "#dcfce7", stroke: "#22c55e", strokeWidth: 2, cornerRadius: 8 },
      { type: ELEMENT_TYPES.TEXT, x: 40, y: 112, text: "Nom", fontSize: 18, fill: "#92400e" },
      { type: ELEMENT_TYPES.TEXT, x: 140, y: 112, text: "Verbe", fontSize: 18, fill: "#1e40af" },
      { type: ELEMENT_TYPES.TEXT, x: 250, y: 112, text: "Adj.", fontSize: 18, fill: "#166534" },
    ]
  },
  { 
    id: "carte_pince", 
    name: "Carte a pince", 
    icon: "📌",
    elements: [
      // Card background
      { type: ELEMENT_TYPES.RECT, x: 20, y: 20, width: 300, height: 240, fill: "#ffffff", stroke: "#6366f1", strokeWidth: 3, cornerRadius: 12 },
      // Question area
      { type: ELEMENT_TYPES.RECT, x: 30, y: 30, width: 280, height: 60, fill: "#eef2ff", stroke: "transparent", strokeWidth: 0, cornerRadius: 8 },
      { type: ELEMENT_TYPES.TEXT, x: 45, y: 45, text: "Question ici", fontSize: 20, fill: "#4338ca", fontFamily: "Fredoka", width: 250 },
      // Answer options with pin circles
      { type: ELEMENT_TYPES.CIRCLE, x: 280, y: 120, radius: 15, fill: "#ffffff", stroke: "#6366f1", strokeWidth: 2 },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 108, text: "Reponse A", fontSize: 16, fill: "#1e293b" },
      { type: ELEMENT_TYPES.CIRCLE, x: 280, y: 160, radius: 15, fill: "#ffffff", stroke: "#6366f1", strokeWidth: 2 },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 148, text: "Reponse B", fontSize: 16, fill: "#1e293b" },
      { type: ELEMENT_TYPES.CIRCLE, x: 280, y: 200, radius: 15, fill: "#ffffff", stroke: "#6366f1", strokeWidth: 2 },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 188, text: "Reponse C", fontSize: 16, fill: "#1e293b" },
      { type: ELEMENT_TYPES.CIRCLE, x: 280, y: 240, radius: 15, fill: "#22c55e", stroke: "#16a34a", strokeWidth: 2 },
      { type: ELEMENT_TYPES.TEXT, x: 50, y: 228, text: "Bonne reponse", fontSize: 16, fill: "#166534" },
    ]
  },
  { 
    id: "puzzle_4", 
    name: "Puzzle 4 pcs", 
    icon: "🧩",
    elements: [
      // 2x2 puzzle grid
      { type: ELEMENT_TYPES.RECT, x: 40, y: 40, width: 120, height: 100, fill: "#fef3c7", stroke: "#f59e0b", strokeWidth: 2, cornerRadius: 0 },
      { type: ELEMENT_TYPES.RECT, x: 160, y: 40, width: 120, height: 100, fill: "#dbeafe", stroke: "#3b82f6", strokeWidth: 2, cornerRadius: 0 },
      { type: ELEMENT_TYPES.RECT, x: 40, y: 140, width: 120, height: 100, fill: "#dcfce7", stroke: "#22c55e", strokeWidth: 2, cornerRadius: 0 },
      { type: ELEMENT_TYPES.RECT, x: 160, y: 140, width: 120, height: 100, fill: "#fce7f3", stroke: "#ec4899", strokeWidth: 2, cornerRadius: 0 },
      { type: ELEMENT_TYPES.TEXT, x: 70, y: 75, text: "1", fontSize: 32, fill: "#92400e", fontFamily: "Fredoka" },
      { type: ELEMENT_TYPES.TEXT, x: 200, y: 75, text: "2", fontSize: 32, fill: "#1e40af", fontFamily: "Fredoka" },
      { type: ELEMENT_TYPES.TEXT, x: 70, y: 175, text: "3", fontSize: 32, fill: "#166534", fontFamily: "Fredoka" },
      { type: ELEMENT_TYPES.TEXT, x: 200, y: 175, text: "4", fontSize: 32, fill: "#be185d", fontFamily: "Fredoka" },
    ]
  },
  { 
    id: "seyes_writing", 
    name: "Ecriture Seyes", 
    icon: "✏️",
    elements: [
      { type: ELEMENT_TYPES.SEYES, x: 30, y: 50, width: 280, height: 180, lineColor: "#c4b5fd", marginColor: "#f87171", lineHeight: 8, showMargin: true },
      { type: ELEMENT_TYPES.TEXT, x: 55, y: 30, text: "Ecris ton prenom :", fontSize: 18, fill: "#1e293b", fontFamily: "Fredoka" },
    ]
  },
  { 
    id: "math_grid", 
    name: "Quadrillage", 
    icon: "📐",
    elements: [
      { type: ELEMENT_TYPES.GRID, x: 60, y: 60, width: 200, height: 160, cellSize: 20, strokeColor: "#94a3b8", strokeWidth: 1, gridType: "square" },
      { type: ELEMENT_TYPES.TEXT, x: 80, y: 30, text: "Mathematiques", fontSize: 20, fill: "#1e293b", fontFamily: "Fredoka" },
    ]
  },
  { 
    id: "tableau_3x3", 
    name: "Tableau 3x3", 
    icon: "📊",
    elements: [
      { type: ELEMENT_TYPES.TABLE, x: 50, y: 50, width: 240, height: 180, rows: 3, cols: 3, strokeColor: "#1e293b", strokeWidth: 2, fillColor: "#ffffff", headerColor: "#dbeafe" },
    ]
  },
  { 
    id: "qcm_interactif", 
    name: "QCM Interactif", 
    icon: "🎯",
    elements: [
      { type: ELEMENT_TYPES.QCM_INTERACTIVE, x: 30, y: 30, width: 280, height: 220, question: "Combien font 2 + 2 ?", options: ["3", "4", "5"], correctIndex: 1, showFeedback: true, feedbackSound: true },
    ]
  },
  { 
    id: "audio_tts", 
    name: "Audio TTS", 
    icon: "🔊",
    elements: [
      { type: ELEMENT_TYPES.AUDIO, x: 50, y: 100, width: 200, height: 70, audioType: "tts", text: "Bonjour, je suis Navi !", bgColor: "#8b5cf6" },
    ]
  },
];

// Cliparts data
const CLIPARTS = [
  { id: "star", url: "https://api.iconify.design/twemoji/star.svg" },
  { id: "heart", url: "https://api.iconify.design/twemoji/red-heart.svg" },
  { id: "sun", url: "https://api.iconify.design/twemoji/sun.svg" },
  { id: "cat", url: "https://api.iconify.design/twemoji/cat.svg" },
  { id: "dog", url: "https://api.iconify.design/twemoji/dog.svg" },
  { id: "book", url: "https://api.iconify.design/twemoji/books.svg" },
  { id: "check", url: "https://api.iconify.design/twemoji/check-mark-button.svg" },
  { id: "cross", url: "https://api.iconify.design/twemoji/cross-mark.svg" },
];

// QR Code Konva Element - generates real QR code from data
function QRCodeKonvaElement({ element, isSelected, onSelect, onDragEnd, onTransform }) {
  const [qrImage, setQrImage] = useState(null);
  
  useEffect(() => {
    const generateQR = async () => {
      try {
        const dataUrl = await QRCodeLib.toDataURL(element.qrData || "https://example.com", {
          width: Math.max(element.width, element.height) * 2, // Higher res for quality
          margin: 1,
          color: {
            dark: element.fgColor || "#000000",
            light: element.bgColor || "#ffffff",
          },
        });
        
        const img = new window.Image();
        img.onload = () => setQrImage(img);
        img.src = dataUrl;
      } catch (err) {
        console.error("QR generation error:", err);
      }
    };
    
    generateQR();
  }, [element.qrData, element.width, element.height, element.fgColor, element.bgColor]);
  
  return (
    <ResizableElement
      element={element}
      isSelected={isSelected}
      onSelect={onSelect}
      onDragEnd={onDragEnd}
      onTransform={onTransform}
      keepRatio={true}
    >
      <Group>
        {qrImage ? (
          <KonvaImage
            x={0}
            y={0}
            image={qrImage}
            width={element.width}
            height={element.height}
          />
        ) : (
          <>
            <Rect
              x={0}
              y={0}
              width={element.width}
              height={element.height}
              fill={element.bgColor || "#ffffff"}
              stroke="#e2e8f0"
              strokeWidth={1}
            />
            <Text
              x={0}
              y={element.height / 2 - 10}
              text="Chargement..."
              fontSize={12}
              fill="#94a3b8"
              width={element.width}
              align="center"
            />
          </>
        )}
      </Group>
    </ResizableElement>
  );
}

// Main Editor Component
export default function ContentEditorPage() {
  const navigate = useNavigate();
  const { resourceId: urlResourceId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [documentTitle, setDocumentTitle] = useState("Sans titre");
  const [documentId, setDocumentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [loadedImages, setLoadedImages] = useState({});
  const [resourceLoading, setResourceLoading] = useState(false);
  
  // Mobile panels
  const [showTools, setShowTools] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCliparts, setShowCliparts] = useState(false);

  // Page format and multi-page
  const [pageFormat, setPageFormat] = useState("A4");
  const [pageOrientation, setPageOrientation] = useState(ORIENTATIONS.PORTRAIT);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [pages, setPages] = useState([{ id: uuidv4(), elements: [], thumbnail: null }]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  
  // Export dialog
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // Target assignment (students)
  const [showTargetPanel, setShowTargetPanel] = useState(false);
  const [targetConfig, setTargetConfig] = useState({ type: "generic", studentIds: [] });

  // Export preview (fullscreen)
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // Library Panel (Phase C - replaces modal)
  const [showLibraryPanel, setShowLibraryPanel] = useState(false);
  
  // Phase D - Advanced Tools
  const [showAdvancedTools, setShowAdvancedTools] = useState(false);
  const [showPDFComposer, setShowPDFComposer] = useState(false);
  
  // ClipArt Library (legacy modal - keep for backward compatibility)
  const [showClipArtLibrary, setShowClipArtLibrary] = useState(false);

  // Context menu and clipboard
  const [contextMenuPos, setContextMenuPos] = useState(null);
  const [clipboard, setClipboard] = useState(null);

  // Resource metadata sheet (for proper save to resources API)
  const [showMetadataSheet, setShowMetadataSheet] = useState(false);
  const [resourceMetadata, setResourceMetadata] = useState(null);

  // Right sidebar tab (properties or layers)
  const [rightSidebarTab, setRightSidebarTab] = useState("properties"); // "properties" | "layers"

  // Tool placement mode - when a tool is selected but not yet placed
  const [pendingTool, setPendingTool] = useState(null); // null | ELEMENT_TYPES.*

  // Selection bounds - single source of truth for element bounding box
  // Recalculated when selectedId changes or after any transformation
  const [selectionBounds, setSelectionBounds] = useState(null);
  
  // Selection mode - distinguishes between different interaction states
  // "idle" = no selection, "selected" = simple selection with handles, 
  // "editing" = inline text editing, "transforming" = resize/rotate in progress
  const [selectionMode, setSelectionMode] = useState("idle"); // "idle" | "selected" | "editing" | "transforming"

  // Inline text editing state
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState("");
  const textInputRef = useRef(null);

  // Canvas dimensions - responsive to page format
  const [canvasSize, setCanvasSize] = useState({ width: 340, height: 480 });
  const [canvasScale, setCanvasScale] = useState(1);

  // Update canvas size based on page format
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const padding = 40;
        
        // Get page dimensions from format
        const pageDims = getPageDimensions(pageFormat, pageOrientation);
        
        // Scale to fit container
        const scaled = getScaledDimensions(
          pageDims.width,
          pageDims.height,
          rect.width,
          rect.height,
          padding
        );
        
        setCanvasSize({ width: Math.floor(scaled.width), height: Math.floor(scaled.height) });
        setCanvasScale(scaled.scale);
      }
    };
    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [pageFormat, pageOrientation]);

  // Load existing resource if resourceId is in URL
  useEffect(() => {
    const loadResource = async () => {
      if (!urlResourceId) return;
      
      setResourceLoading(true);
      try {
        const { data: resource } = await getResource(urlResourceId);
        setDocumentId(resource.id);
        setDocumentTitle(resource.title);
        
        // Load elements
        if (resource.elements && resource.elements.length > 0) {
          setElements(resource.elements);
          addToHistory(resource.elements);
        }
        
        // Load pages if multi-page
        if (resource.pages) {
          setPages(resource.pages);
        }
        
        // Load page settings
        if (resource.page_settings) {
          setPageFormat(resource.page_settings.format || "A4");
          setPageOrientation(resource.page_settings.orientation || "portrait");
        }
        
        // Store metadata for the sheet
        setResourceMetadata({
          niveau: resource.niveau,
          matiere: resource.matiere,
          resource_type: resource.resource_type,
          status: resource.status,
          tags: resource.tags || [],
          skills: resource.skills || [],
          target: resource.target,
        });
        
        // Load images from elements
        resource.elements?.forEach((el) => {
          if (el.type === ELEMENT_TYPES.IMAGE && el.src) {
            const img = new window.Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              setLoadedImages(prev => ({ ...prev, [el.id]: img }));
            };
            img.src = el.src;
          }
        });
        
        toast.success("Ressource chargee");
      } catch (err) {
        console.error("Error loading resource:", err);
        toast.error("Erreur lors du chargement de la ressource");
      } finally {
        setResourceLoading(false);
      }
    };
    
    loadResource();
  }, [urlResourceId]);

  // Load session data to prefill metadata when creating from a session
  useEffect(() => {
    const loadSessionData = async () => {
      if (!sessionId || urlResourceId) return; // Only for new resources from session
      
      try {
        const { data: session } = await getSession(sessionId);
        
        // Prefill metadata from session data
        setResourceMetadata(prev => ({
          ...prev,
          matiere: session.subject || null,
          niveau: session.niveau || null,
          skills_repository_id: session.skills_repository_id || null,
          skill_ids: session.skill_ids || [],
          // Store session reference for later linking
          _sessionId: sessionId,
        }));
        
        // Set document title from session title if provided
        if (session.title) {
          setDocumentTitle(session.title);
        }
        
        toast.info("Données de la séance pré-remplies");
      } catch (err) {
        console.error("Error loading session data:", err);
        // Silent fail - user can still fill metadata manually
      }
    };
    
    loadSessionData();
  }, [sessionId, urlResourceId]);

  const selectedElement = elements.find(el => el.id === selectedId);

  // Calculate element bounds for selection box and floating toolbar positioning
  // This is the SINGLE SOURCE OF TRUTH for element bounding box
  const calculateElementBounds = useCallback((element) => {
    if (!element) return null;
    
    let width, height, x, y;
    
    if (element.type === ELEMENT_TYPES.CIRCLE) {
      const radius = element.radius || 40;
      width = radius * 2;
      height = radius * 2;
      x = element.x || 0;
      y = element.y || 0;
    } else {
      width = element.width || 100;
      height = element.height || (element.type === ELEMENT_TYPES.TEXT ? 30 : 100);
      x = element.x || 0;
      y = element.y || 0;
    }
    
    return { x, y, width, height };
  }, []);

  // Update selection bounds and mode whenever selectedElement changes
  useEffect(() => {
    if (selectedElement) {
      const bounds = calculateElementBounds(selectedElement);
      setSelectionBounds(bounds);
      // Transition to "selected" mode if not already editing
      if (selectionMode === "idle") {
        setSelectionMode("selected");
      }
    } else {
      setSelectionBounds(null);
      setSelectionMode("idle");
    }
  }, [selectedElement, calculateElementBounds, selectionMode]);

  // History
  const addToHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newElements));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(history[historyIndex - 1]));
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(JSON.parse(history[historyIndex + 1]));
    }
  }, [history, historyIndex]);

  // Add element
  const addElement = useCallback((type, position = null) => {
    const id = uuidv4();
    const defaults = DEFAULT_PROPS[type] || {};
    
    // If position is provided, use it; otherwise center the element
    const x = position ? position.x - (defaults.width || 100) / 2 : canvasSize.width / 2 - 60;
    const y = position ? position.y - (defaults.height || 80) / 2 : canvasSize.height / 2 - 40;
    
    const newElement = {
      id,
      type,
      x,
      y,
      rotation: 0,
      opacity: 1,
      ...defaults,
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
    setShowTools(false);
    setPendingTool(null); // Clear pending tool after placement
  }, [elements, addToHistory, canvasSize]);

  // Select tool for placement (new click-to-place behavior)
  const selectToolForPlacement = useCallback((type) => {
    setPendingTool(type);
    setSelectedId(null);
    setShowTools(false);
  }, []);

  // Update element
  const updateElement = useCallback((id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
  }, [elements]);

  const finishUpdate = useCallback(() => {
    addToHistory(elements);
  }, [elements, addToHistory]);

  // Delete element
  const deleteElement = useCallback((id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(null);
    setShowProperties(false);
  }, [elements, addToHistory]);

  // Copy element to clipboard
  const copyElement = useCallback(() => {
    if (selectedElement) {
      setClipboard({ ...selectedElement });
    }
  }, [selectedElement]);

  // Cut element
  const cutElement = useCallback(() => {
    if (selectedElement) {
      setClipboard({ ...selectedElement });
      deleteElement(selectedElement.id);
    }
  }, [selectedElement, deleteElement]);

  // Paste element
  const pasteElement = useCallback(() => {
    if (clipboard) {
      const newElement = {
        ...clipboard,
        id: uuidv4(),
        x: (clipboard.x || 50) + 20,
        y: (clipboard.y || 50) + 20,
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      addToHistory(newElements);
      setSelectedId(newElement.id);
    }
  }, [clipboard, elements, addToHistory]);

  // Duplicate element
  const duplicateElement = useCallback(() => {
    if (selectedElement) {
      const newElement = {
        ...selectedElement,
        id: uuidv4(),
        x: (selectedElement.x || 50) + 20,
        y: (selectedElement.y || 50) + 20,
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      addToHistory(newElements);
      setSelectedId(newElement.id);
    }
  }, [selectedElement, elements, addToHistory]);

  // Fit element to page width
  const fitToPageWidth = useCallback(() => {
    if (selectedElement) {
      const margin = 20;
      updateElement(selectedElement.id, {
        x: margin,
        width: canvasSize.width - (margin * 2),
      });
      finishUpdate();
    }
  }, [selectedElement, updateElement, finishUpdate, canvasSize]);

  // Bring element forward
  const bringForward = useCallback(() => {
    if (!selectedElement) return;
    const index = elements.findIndex(el => el.id === selectedElement.id);
    if (index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      setElements(newElements);
      addToHistory(newElements);
    }
  }, [selectedElement, elements, addToHistory]);

  // Send element backward
  const sendBackward = useCallback(() => {
    if (!selectedElement) return;
    const index = elements.findIndex(el => el.id === selectedElement.id);
    if (index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      setElements(newElements);
      addToHistory(newElements);
    }
  }, [selectedElement, elements, addToHistory]);

  // Inline text editing handlers
  const startTextEditing = useCallback((elementId) => {
    const element = elements.find(el => el.id === elementId);
    if (element && element.type === ELEMENT_TYPES.TEXT) {
      setEditingTextId(elementId);
      setEditingTextValue(element.text || "");
      setSelectionMode("editing"); // Transition to editing mode
      // Focus input after state update
      setTimeout(() => {
        if (textInputRef.current) {
          textInputRef.current.focus();
          textInputRef.current.select();
        }
      }, 50);
    }
  }, [elements]);

  const finishTextEditing = useCallback(() => {
    if (editingTextId && editingTextValue !== undefined) {
      updateElement(editingTextId, { text: editingTextValue });
      finishUpdate();
    }
    setEditingTextId(null);
    setEditingTextValue("");
    setSelectionMode("selected"); // Return to selected mode
  }, [editingTextId, editingTextValue, updateElement, finishUpdate]);

  const cancelTextEditing = useCallback(() => {
    setEditingTextId(null);
    setEditingTextValue("");
    setSelectionMode("selected"); // Return to selected mode
  }, []);

  // Handle text double-click for inline editing
  const handleTextDoubleClick = useCallback((elementId) => {
    startTextEditing(elementId);
  }, [startTextEditing]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case "c":
            e.preventDefault();
            copyElement();
            break;
          case "x":
            e.preventDefault();
            cutElement();
            break;
          case "v":
            e.preventDefault();
            pasteElement();
            break;
          case "d":
            e.preventDefault();
            duplicateElement();
            break;
          case "z":
            e.preventDefault();
            undo();
            break;
          case "y":
            e.preventDefault();
            redo();
            break;
        }
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedElement) {
          e.preventDefault();
          deleteElement(selectedElement.id);
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [copyElement, cutElement, pasteElement, duplicateElement, undo, redo, selectedElement, deleteElement]);

  // Apply template
  const applyTemplate = useCallback((template) => {
    const newElements = template.elements.map(el => ({
      ...el,
      id: uuidv4(),
    }));
    setElements(newElements);
    addToHistory(newElements);
    setShowTemplates(false);
  }, [addToHistory]);

  // Add clipart
  const addClipart = useCallback((clipart) => {
    const id = uuidv4();
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setLoadedImages(prev => ({ ...prev, [id]: img }));
    };
    img.src = clipart.url;
    
    const newElement = {
      id,
      type: ELEMENT_TYPES.IMAGE,
      x: canvasSize.width / 2 - 40,
      y: canvasSize.height / 2 - 40,
      width: 80,
      height: 80,
      src: clipart.url,
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
    setShowCliparts(false);
  }, [elements, addToHistory, canvasSize]);

  // Add pedagogic block (label, frame, table, etc.)
  const addPedagogicBlock = useCallback((block) => {
    const id = uuidv4();
    const { style, type: blockType, defaultText } = block;
    
    let newElement;
    
    // Handle different block types
    if (blockType === "label") {
      // Create a group with rectangle + text
      newElement = {
        id,
        type: ELEMENT_TYPES.RECT,
        x: canvasSize.width / 2 - style.width / 2,
        y: canvasSize.height / 2 - style.height / 2,
        width: style.width,
        height: style.height,
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth: 2,
        cornerRadius: style.radius || 0,
        dash: style.dash,
        // Store metadata for later use
        blockType: "label",
        labelText: defaultText || "Mot",
      };
    } else if (blockType === "frame" || blockType === "answerbox" || blockType === "checkbox" || blockType === "numberbox") {
      newElement = {
        id,
        type: ELEMENT_TYPES.RECT,
        x: canvasSize.width / 2 - style.width / 2,
        y: canvasSize.height / 2 - style.height / 2,
        width: style.width,
        height: style.height,
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth || 2,
        cornerRadius: style.radius || 0,
        dash: style.dash,
        blockType,
      };
    } else if (blockType === "table") {
      // Create a table as multiple rectangles
      const elements = [];
      const totalWidth = style.cols * style.cellWidth;
      const totalHeight = style.rows * style.cellHeight;
      const startX = canvasSize.width / 2 - totalWidth / 2;
      const startY = canvasSize.height / 2 - totalHeight / 2;
      
      // Create outer frame
      newElement = {
        id,
        type: ELEMENT_TYPES.RECT,
        x: startX,
        y: startY,
        width: totalWidth,
        height: totalHeight,
        fill: "#ffffff",
        stroke: style.stroke,
        strokeWidth: 1,
        cornerRadius: 0,
        blockType: "table",
        tableCols: style.cols,
        tableRows: style.rows,
        cellWidth: style.cellWidth,
        cellHeight: style.cellHeight,
      };
    } else if (blockType === "seyes" || blockType === "simplelines") {
      // Create lined writing area
      newElement = {
        id,
        type: ELEMENT_TYPES.RECT,
        x: canvasSize.width / 2 - style.width / 2,
        y: canvasSize.height / 2 - style.height / 2,
        width: style.width,
        height: style.height,
        fill: "#ffffff",
        stroke: "#3b82f6",
        strokeWidth: 1,
        cornerRadius: 0,
        blockType,
        lineCount: style.lineCount,
        lineSpacing: style.lineSpacing || (style.height / style.lineCount),
      };
    } else {
      // Default rectangle
      newElement = {
        id,
        type: ELEMENT_TYPES.RECT,
        x: canvasSize.width / 2 - 100,
        y: canvasSize.height / 2 - 50,
        width: 200,
        height: 100,
        fill: "#ffffff",
        stroke: "#374151",
        strokeWidth: 2,
      };
    }
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
  }, [elements, addToHistory, canvasSize]);

  // Add AI text
  const addAIText = useCallback((aiResult) => {
    const id = uuidv4();
    const text = aiResult.text || aiResult.title || "Texte genere";
    
    const newElement = {
      id,
      type: ELEMENT_TYPES.TEXT,
      x: 20,
      y: 20,
      text: text.substring(0, 200),
      fontSize: 14,
      fontFamily: "Nunito",
      fill: "#1e293b",
      width: canvasSize.width - 40,
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
    if (aiResult.title) setDocumentTitle(aiResult.title);
  }, [elements, addToHistory, canvasSize]);

  // ============================================================
  // PHASE D - ADVANCED TOOLS HANDLERS
  // ============================================================

  // Add watermark element
  const addWatermark = useCallback((watermarkConfig) => {
    const id = uuidv4();
    const newElement = {
      id,
      type: ELEMENT_TYPES.TEXT,
      x: canvasSize.width / 2 - 150,
      y: canvasSize.height / 2 - 30,
      text: watermarkConfig.text,
      fontSize: watermarkConfig.fontSize,
      fontFamily: "Arial, sans-serif",
      fill: watermarkConfig.color,
      opacity: watermarkConfig.opacity,
      rotation: watermarkConfig.rotation,
      width: 300,
      align: "center",
      isWatermark: true, // Mark as watermark for special handling
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
    toast.success("Filigrane ajouté");
  }, [elements, addToHistory, canvasSize]);

  // Add stamp element
  const addStamp = useCallback((stampConfig) => {
    const id = uuidv4();
    const newElement = {
      id,
      type: ELEMENT_TYPES.RECT,
      x: canvasSize.width - 150,
      y: canvasSize.height - 100,
      width: 120,
      height: 60,
      fill: stampConfig.bgColor,
      stroke: stampConfig.color,
      strokeWidth: 3,
      cornerRadius: 12,
      isStamp: true,
      stampIcon: stampConfig.icon,
      stampText: stampConfig.text,
      stampColor: stampConfig.color,
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(id);
    toast.success("Tampon ajouté");
  }, [elements, addToHistory, canvasSize]);

  // Add page numbers to all pages
  const addPageNumbers = useCallback((config) => {
    const position = config.position;
    const format = config.format;
    const startNum = config.startNumber;
    
    // Add text element for page number
    const id = uuidv4();
    let x = 20, y = canvasSize.height - 30;
    
    if (position.includes("center")) x = canvasSize.width / 2 - 20;
    if (position.includes("right")) x = canvasSize.width - 60;
    if (position.includes("top")) y = 20;
    
    const pageNum = currentPageIndex + startNum;
    const totalPagesNum = pages.length;
    let text = String(pageNum);
    
    switch (format) {
      case "of-total": text = `${pageNum}/${totalPagesNum}`; break;
      case "page-x": text = `Page ${pageNum}`; break;
      case "page-x-of-y": text = `Page ${pageNum} sur ${totalPagesNum}`; break;
      case "dash": text = `- ${pageNum} -`; break;
      default: text = String(pageNum);
    }

    const newElement = {
      id,
      type: ELEMENT_TYPES.TEXT,
      x,
      y,
      text,
      fontSize: 12,
      fontFamily: "Arial, sans-serif",
      fill: "#64748b",
      width: 100,
      align: position.includes("center") ? "center" : position.includes("right") ? "right" : "left",
      isPageNumber: true,
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    toast.success("Numérotation ajoutée");
  }, [elements, addToHistory, canvasSize, currentPageIndex, pages.length]);

  // Apply N-up layout
  const applyNup = useCallback((config) => {
    // N-up creates a new resource with multiple pages combined
    toast.info(`Mise en page ${config.layout.cols}×${config.layout.rows} - Fonctionnalité export en cours de développement`);
  }, []);

  // Add overlay element
  const addOverlay = useCallback((config) => {
    if (config.type === "text") {
      const id = uuidv4();
      const newElement = {
        id,
        type: ELEMENT_TYPES.TEXT,
        x: canvasSize.width / 2 - 100,
        y: 20,
        text: "Texte en superposition",
        fontSize: 16,
        fontFamily: "Arial, sans-serif",
        fill: "#1e293b",
        width: 200,
        align: "center",
        isOverlay: true,
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      addToHistory(newElements);
      setSelectedId(id);
      toast.success("Texte de superposition ajouté");
    } else if (config.type === "image") {
      // Trigger image upload
      toast.info("Sélectionnez une image pour la superposition");
    }
    setShowAdvancedTools(false);
  }, [elements, addToHistory, canvasSize]);

  // Insert PDF crop as image
  const insertPDFCrop = useCallback((cropData) => {
    const id = uuidv4();
    
    // Create image element
    const img = new window.Image();
    img.onload = () => {
      const scale = Math.min(
        (canvasSize.width - 40) / cropData.width,
        (canvasSize.height - 40) / cropData.height,
        1
      );
      
      const newElement = {
        id,
        type: ELEMENT_TYPES.IMAGE,
        x: 20,
        y: 20,
        width: cropData.width * scale,
        height: cropData.height * scale,
        src: cropData.src,
        isPDFImport: true,
        sourceFile: cropData.sourceFile,
        sourcePage: cropData.sourcePage,
      };

      const newElements = [...elements, newElement];
      setElements(newElements);
      addToHistory(newElements);
      setSelectedId(id);
      toast.success(`Page ${cropData.sourcePage} importée depuis le PDF`);
    };
    img.src = cropData.src;
  }, [elements, addToHistory, canvasSize]);

  // ============================================================
  // PAGE MANAGEMENT FUNCTIONS
  // ============================================================
  
  // Handle page format change
  const handlePageSettingsApply = useCallback((settings) => {
    setPageFormat(settings.format);
    setPageOrientation(settings.orientation);
  }, []);

  // Multi-page management
  const handleAddPage = useCallback(() => {
    const newPage = { id: uuidv4(), elements: [], thumbnail: null };
    setPages(prev => [...prev, newPage]);
    setCurrentPageIndex(pages.length);
    setElements([]);
    setSelectedId(null);
  }, [pages.length]);

  const handleDeletePage = useCallback((pageIndex) => {
    if (pages.length <= 1) return;
    
    setPages(prev => prev.filter((_, i) => i !== pageIndex));
    
    if (pageIndex <= currentPageIndex && currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
    
    // Load elements from new current page
    const newIndex = Math.min(pageIndex, pages.length - 2);
    if (pages[newIndex]) {
      setElements(pages[newIndex].elements || []);
    }
  }, [pages, currentPageIndex]);

  const handleDuplicatePage = useCallback((pageIndex) => {
    const pageToDuplicate = pages[pageIndex];
    const newPage = {
      id: uuidv4(),
      elements: pageToDuplicate.elements.map(el => ({ ...el, id: uuidv4() })),
      thumbnail: null,
    };
    
    const newPages = [...pages];
    newPages.splice(pageIndex + 1, 0, newPage);
    setPages(newPages);
  }, [pages]);

  const handlePageChange = useCallback((newIndex) => {
    // Save current page elements
    setPages(prev => prev.map((page, i) => 
      i === currentPageIndex ? { ...page, elements } : page
    ));
    
    // Load new page elements
    setCurrentPageIndex(newIndex);
    setElements(pages[newIndex]?.elements || []);
    setSelectedId(null);
  }, [currentPageIndex, elements, pages]);

  const handleReorderPages = useCallback((newPages, newCurrentIndex) => {
    setPages(newPages);
    setCurrentPageIndex(newCurrentIndex);
  }, []);

  // ============================================================
  // EXPORT FUNCTIONS
  // ============================================================

  const handleGenerateVariants = useCallback(async (studentIds) => {
    if (!documentId) {
      toast.error("Sauvegardez d'abord le document avant de generer des variantes");
      return;
    }
    
    try {
      const { data } = await generateVariants(documentId, studentIds);
      toast.success(
        `${data?.variants_count || studentIds.length} variante(s) generee(s) avec succes !`,
        {
          description: "Les eleves pourront acceder a leurs versions personnalisees",
          duration: 5000,
        }
      );
    } catch (err) {
      console.error("Error generating variants:", err);
      toast.error("Erreur lors de la generation des variantes", {
        description: err.response?.data?.detail || "Verifiez que les eleves ont des profils configures",
      });
    }
  }, [documentId]);

  const handleTargetChange = useCallback((newTarget) => {
    setTargetConfig(newTarget);
  }, []);


  // Save - Opens metadata sheet for proper resource creation
  const saveDocument = useCallback(() => {
    setShowMetadataSheet(true);
  }, []);

  // Handle save from metadata sheet
  const handleResourceSaved = useCallback((savedResource) => {
    setDocumentId(savedResource.id);
    setDocumentTitle(savedResource.title);
    setResourceMetadata({
      niveau: savedResource.niveau,
      matiere: savedResource.matiere,
      resource_type: savedResource.resource_type,
      status: savedResource.status,
      tags: savedResource.tags || [],
      skills: savedResource.skills || [],
      target: savedResource.target,
    });
    
    // Update URL to include resource ID for future edits
    if (!urlResourceId && savedResource.id) {
      navigate(`/editor/${savedResource.id}`, { replace: true });
    }
  }, [navigate, urlResourceId]);

  // Export PNG
  const exportImage = () => {
    const stage = stageRef.current;
    if (!stage) return;
    setSelectedId(null);
    setTimeout(() => {
      const dataURL = stage.toDataURL({ pixelRatio: 2 });
      const link = document.createElement("a");
      link.download = `${documentTitle}.png`;
      link.href = dataURL;
      link.click();
    }, 100);
  };

  // Export PDF
  const [exportingPDF, setExportingPDF] = useState(false);
  
  const exportPDF = async () => {
    const stage = stageRef.current;
    if (!stage) return;
    
    setExportingPDF(true);
    setSelectedId(null);
    
    try {
      // Wait for selection to clear
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Get high-res canvas image
      const dataURL = stage.toDataURL({ pixelRatio: 3 });
      
      // Call backend API
      const { exportDocumentPDF } = await import("../lib/api");
      const response = await exportDocumentPDF(dataURL, documentTitle, "portrait");
      
      // Download PDF
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentTitle}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Erreur lors de l'export PDF");
    }
    
    setExportingPDF(false);
  };

  // Export Grouped PDF (2, 4, 8, 16 per page)
  const handleExportGrouped = async (grouping, format) => {
    const stage = stageRef.current;
    if (!stage) return;
    
    setExportingPDF(true);
    setSelectedId(null);
    
    try {
      // Wait for selection to clear
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Collect images from all pages (or current page multiple times for demo)
      const images = [];
      
      // For multi-page documents, we'd iterate through pages
      // For now, we use the current canvas state
      for (let i = 0; i < Math.max(pages.length, 1); i++) {
        const dataURL = stage.toDataURL({ pixelRatio: 2 });
        images.push(dataURL);
      }
      
      if (format === "pdf") {
        // Call grouped PDF endpoint
        const { exportDocumentPDFGrouped } = await import("../lib/api");
        const response = await exportDocumentPDFGrouped(
          images, 
          documentTitle, 
          grouping, 
          pageOrientation
        );
        
        // Download PDF
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentTitle}_groupe_${grouping}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Export as PNG (single image)
        exportImage();
      }
    } catch (err) {
      console.error("Grouped export failed:", err);
      alert("Erreur lors de l'export groupe");
    }
    
    setExportingPDF(false);
  };

  // Direct print function
  const handlePrint = async () => {
    const stage = stageRef.current;
    if (!stage) return;
    
    setExportingPDF(true);
    setSelectedId(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 150));
      const dataURL = stage.toDataURL({ pixelRatio: 2 });
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${documentTitle}</title>
            <style>
              @page { size: ${pageOrientation === 'landscape' ? 'A4 landscape' : 'A4'}; margin: 10mm; }
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <img src="${dataURL}" onload="window.print(); window.close();" />
          </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (err) {
      console.error("Print error:", err);
      alert("Erreur lors de l'impression");
    }
    
    setExportingPDF(false);
  };

  // Handle stage click
  const handleStageClick = (e) => {
    // Close context menu on regular click
    setContextMenuPos(null);
    
    // Check if click is on empty area (Stage or background Rect)
    const isEmptyAreaClick = e.target === e.target.getStage() || 
      (e.target.getClassName && e.target.getClassName() === 'Rect' && e.target.attrs && e.target.attrs.fill === '#ffffff');
    
    // If a tool is pending placement, create the element at click position
    if (pendingTool && isEmptyAreaClick) {
      const stage = stageRef.current;
      if (stage) {
        const pos = stage.getPointerPosition();
        addElement(pendingTool, pos);
      }
      return;
    }
    
    if (isEmptyAreaClick) {
      setSelectedId(null);
      setPendingTool(null); // Cancel pending tool if clicking empty area without placement
    }
  };

  // Handle right-click for context menu
  const handleContextMenu = useCallback((e) => {
    e.evt.preventDefault();
    
    // Get clicked position
    const stage = stageRef.current;
    if (!stage) return;
    
    const pos = stage.getPointerPosition();
    
    // If clicked on a shape, select it
    const clickedOnEmpty = e.target === stage;
    if (!clickedOnEmpty) {
      // Find which element was clicked
      const clickedElement = elements.find(el => {
        // Simple bounds check
        const elX = el.x || 0;
        const elY = el.y || 0;
        const elWidth = el.width || el.radius * 2 || 100;
        const elHeight = el.height || el.radius * 2 || 100;
        return pos.x >= elX && pos.x <= elX + elWidth && pos.y >= elY && pos.y <= elY + elHeight;
      });
      
      if (clickedElement) {
        setSelectedId(clickedElement.id);
      }
    }
    
    // Show context menu at mouse position
    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    setContextMenuPos({
      x: e.evt.clientX,
      y: e.evt.clientY,
    });
  }, [elements]);

  return (
    <div data-testid="content-editor-page" className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* Header */}
      <header className="h-14 md:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-3 md:px-4 flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/home")}
            className="p-2 hover:bg-slate-100 rounded-lg touch-target"
          >
            <ChevronLeft size={24} className="text-slate-600" />
          </button>
          
          <input
            data-testid="document-title"
            type="text"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
            className="font-fredoka font-bold text-base md:text-lg text-slate-800 bg-transparent border-none outline-none focus:bg-slate-50 px-2 py-1 rounded flex-1 min-w-0 truncate"
          />
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Resource Metadata Indicator - shows niveau, matiere, status */}
          {resourceMetadata && (resourceMetadata.niveau || resourceMetadata.matiere) && (
            <button
              data-testid="metadata-indicator"
              onClick={() => setShowMetadataSheet(true)}
              className="hidden md:flex items-center gap-1.5 px-2 py-1 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-xs font-medium text-indigo-700 transition-colors"
              title="Metadonnees de la ressource"
            >
              {resourceMetadata.niveau && (
                <span className="bg-indigo-200/50 px-1.5 py-0.5 rounded">{resourceMetadata.niveau}</span>
              )}
              {resourceMetadata.matiere && (
                <span className="text-indigo-600">{resourceMetadata.matiere}</span>
              )}
              {resourceMetadata.status && resourceMetadata.status !== 'draft' && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  resourceMetadata.status === 'published' ? 'bg-green-100 text-green-700' : 
                  resourceMetadata.status === 'archived' ? 'bg-gray-100 text-gray-600' : ''
                }`}>
                  {resourceMetadata.status === 'published' ? 'Publié' : 
                   resourceMetadata.status === 'archived' ? 'Archivé' : ''}
                </span>
              )}
            </button>
          )}

          {/* Page Format Indicator - hidden on mobile */}
          <div className="hidden md:block">
            <PageFormatIndicator
              format={pageFormat}
              orientation={pageOrientation}
              onClick={() => setShowPageSettings(true)}
            />
          </div>

          {/* Target Students Indicator */}
          <div className="hidden lg:block">
            <TargetIndicator
              target={targetConfig}
              onClick={() => setShowTargetPanel(true)}
            />
          </div>

          {/* Undo/Redo - hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <button onClick={undo} disabled={historyIndex <= 0} className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-30">
              <Undo size={20} className="text-slate-600" />
            </button>
            <button onClick={redo} disabled={historyIndex >= history.length - 1} className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-30">
              <Redo size={20} className="text-slate-600" />
            </button>
          </div>

          {/* Students Target Button - mobile */}
          <button
            data-testid="target-btn"
            onClick={() => setShowTargetPanel(true)}
            className="p-2 hover:bg-indigo-100 rounded-lg lg:hidden"
            title="Eleves cibles"
          >
            <Users size={20} className="text-indigo-600" />
          </button>

          {/* Save */}
          <button
            data-testid="save-btn"
            onClick={saveDocument}
            disabled={saving}
            className="p-2 md:px-4 md:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg md:rounded-xl flex items-center gap-2"
          >
            <Save size={20} />
            <span className="hidden md:inline font-bold">Sauver</span>
          </button>

          {/* Export Advanced - opens dialog */}
          <button
            data-testid="export-advanced-btn"
            onClick={() => setShowExportDialog(true)}
            className="p-2 md:px-3 md:py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg md:rounded-xl flex items-center gap-2"
            title="Export avance"
          >
            <Download size={20} />
            <span className="hidden lg:inline font-bold">Exporter</span>
          </button>

          {/* Preview - fullscreen preview */}
          <button
            data-testid="preview-btn"
            onClick={() => setShowPreviewDialog(true)}
            className="p-2 hover:bg-slate-100 rounded-lg hidden md:flex items-center"
            title="Apercu plein ecran"
          >
            <Eye size={20} className="text-slate-600" />
          </button>

          {/* Print - direct print */}
          <button
            data-testid="print-btn"
            onClick={handlePrint}
            disabled={exportingPDF}
            className="p-2 hover:bg-slate-100 rounded-lg hidden lg:flex items-center"
            title="Imprimer"
          >
            <Printer size={20} className="text-slate-600" />
          </button>

          {/* Page Settings Button - mobile */}
          <button
            data-testid="page-settings-btn"
            onClick={() => setShowPageSettings(true)}
            className="p-2 hover:bg-slate-100 rounded-lg md:hidden"
            title="Format de page"
          >
            <RotateCcw size={20} className="text-slate-600" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Desktop sidebar - hidden on mobile */}
        <div className="hidden md:flex w-16 bg-white border-r border-slate-200 flex-col items-center py-4 gap-1.5 flex-shrink-0 overflow-y-auto">
          {[
            { icon: MousePointer, action: () => { setSelectedId(null); setPendingTool(null); }, label: "Sélection", color: "slate", isSelect: true },
            { icon: Type, action: () => selectToolForPlacement(ELEMENT_TYPES.TEXT), label: "Texte", color: "blue", type: ELEMENT_TYPES.TEXT },
            { icon: Square, action: () => selectToolForPlacement(ELEMENT_TYPES.RECT), label: "Rectangle", color: "green", type: ELEMENT_TYPES.RECT },
            { icon: Circle, action: () => selectToolForPlacement(ELEMENT_TYPES.CIRCLE), label: "Cercle", color: "orange", type: ELEMENT_TYPES.CIRCLE },
            { icon: Triangle, action: () => selectToolForPlacement(ELEMENT_TYPES.TRIANGLE), label: "Triangle", color: "teal", type: ELEMENT_TYPES.TRIANGLE },
            { icon: Star, action: () => selectToolForPlacement(ELEMENT_TYPES.STAR), label: "Étoile", color: "yellow", type: ELEMENT_TYPES.STAR },
            { icon: ArrowRight, action: () => selectToolForPlacement(ELEMENT_TYPES.ARROW), label: "Flèche", color: "slate", type: ELEMENT_TYPES.ARROW },
          ].map((tool, i) => (
            <button
              key={i}
              onClick={tool.action}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                pendingTool === tool.type 
                  ? `bg-${tool.color}-500 text-white ring-2 ring-${tool.color}-300 ring-offset-2` 
                  : `bg-${tool.color}-100 hover:bg-${tool.color}-200 text-${tool.color}-600`
              }`}
              title={tool.label}
            >
              <tool.icon size={20} />
            </button>
          ))}
          <div className="border-t border-slate-200 w-10 my-1" />
          {/* Phase B: Highlighter and Freedraw */}
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.HIGHLIGHTER)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.HIGHLIGHTER 
                ? 'bg-yellow-500 text-white ring-2 ring-yellow-300 ring-offset-2' 
                : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
            }`} 
            title="Surligneur"
          >
            <Highlighter size={20} />
          </button>
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.FREEDRAW)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.FREEDRAW 
                ? 'bg-rose-500 text-white ring-2 ring-rose-300 ring-offset-2' 
                : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
            }`} 
            title="Dessin libre"
          >
            <Pencil size={20} />
          </button>
          <div className="border-t border-slate-200 w-10 my-1" />
          {/* Educational tools */}
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.SEYES)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.SEYES 
                ? 'bg-violet-500 text-white ring-2 ring-violet-300 ring-offset-2' 
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`} 
            title="Lignes Seyes"
          >
            <GripVertical size={20} />
          </button>
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.GRID)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.GRID 
                ? 'bg-slate-500 text-white ring-2 ring-slate-300 ring-offset-2' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`} 
            title="Quadrillage"
          >
            <Grid3X3 size={20} />
          </button>
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.TABLE)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.TABLE 
                ? 'bg-cyan-500 text-white ring-2 ring-cyan-300 ring-offset-2' 
                : 'bg-cyan-100 text-cyan-600 hover:bg-cyan-200'
            }`} 
            title="Tableau"
          >
            <Table size={20} />
          </button>
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.QRCODE)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.QRCODE 
                ? 'bg-emerald-500 text-white ring-2 ring-emerald-300 ring-offset-2' 
                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
            }`} 
            title="QR Code"
          >
            <QrCode size={20} />
          </button>
          <div className="border-t border-slate-200 w-10 my-1" />
          {/* Interactive elements */}
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.AUDIO)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.AUDIO 
                ? 'bg-violet-500 text-white ring-2 ring-violet-300 ring-offset-2' 
                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
            }`} 
            title="Audio (TTS/Enregistrement)"
          >
            <Volume2 size={20} />
          </button>
          <button 
            onClick={() => selectToolForPlacement(ELEMENT_TYPES.QCM_INTERACTIVE)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              pendingTool === ELEMENT_TYPES.QCM_INTERACTIVE 
                ? 'bg-indigo-500 text-white ring-2 ring-indigo-300 ring-offset-2' 
                : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
            }`} 
            title="QCM Interactif"
          >
            <CheckCircle size={20} />
          </button>
          <div className="border-t border-slate-200 w-10 my-1" />
          {/* Library Panel Button (Phase C) */}
          <button 
            onClick={() => setShowLibraryPanel(!showLibraryPanel)} 
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              showLibraryPanel 
                ? "bg-gradient-to-br from-pink-500 to-orange-500 text-white ring-2 ring-pink-300 ring-offset-2" 
                : "bg-gradient-to-br from-pink-100 to-orange-100 text-pink-600 hover:from-pink-200 hover:to-orange-200"
            }`} 
            title="Bibliothèque"
          >
            <Library size={20} />
          </button>
          {/* Phase D: Advanced Tools */}
          <button 
            onClick={() => setShowAdvancedTools(true)} 
            className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-600 hover:from-amber-200 hover:to-orange-200 flex items-center justify-center" 
            title="Outils avancés (Filigrane, Tampon, N-up...)"
          >
            <Layers size={20} />
          </button>
          <button 
            onClick={() => setShowPDFComposer(true)} 
            className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-100 to-rose-100 text-red-600 hover:from-red-200 hover:to-rose-200 flex items-center justify-center" 
            title="Composer PDF"
          >
            <FileText size={20} />
          </button>
          <button onClick={() => setShowAI(true)} className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:shadow-lg flex items-center justify-center" title="Assistant IA">
            <Sparkles size={20} />
          </button>
        </div>

        {/* Library Panel (Phase C - persistent left panel) */}
        <LibraryPanel
          isOpen={showLibraryPanel}
          onClose={() => setShowLibraryPanel(false)}
          onSelectClipArt={addClipart}
          onSelectBlock={addPedagogicBlock}
          onSelectTemplate={(template) => {
            toast.info(`Modèle "${template.name}" - Fonctionnalité en cours de développement`);
          }}
          onGenerateAI={() => {
            setShowLibraryPanel(false);
            setShowAI(true);
          }}
        />

        {/* Canvas area with MultiPageManager */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Multi-page manager bar */}
          {pages.length > 0 && (
            <MultiPageManager
              pages={pages}
              currentPageIndex={currentPageIndex}
              onPageChange={handlePageChange}
              onAddPage={handleAddPage}
              onDeletePage={handleDeletePage}
              onDuplicatePage={handleDuplicatePage}
              onReorderPages={handleReorderPages}
            />
          )}
          
          {/* Canvas */}
          <div 
            ref={containerRef}
            className={`flex-1 flex items-center justify-center p-4 overflow-auto bg-slate-100 ${
              pendingTool ? 'cursor-crosshair' : ''
            }`}
          >
            {/* Pending tool indicator */}
            {pendingTool && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2 animate-pulse">
                <Plus size={16} />
                Cliquez sur le canvas pour placer l'élément
                <button 
                  onClick={() => setPendingTool(null)}
                  className="ml-2 p-1 hover:bg-indigo-500 rounded-full"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <div className={`bg-white rounded-2xl shadow-xl overflow-hidden relative ${
              pendingTool ? 'ring-2 ring-indigo-400 ring-offset-4' : ''
            }`}>
              <Stage
                ref={stageRef}
                width={canvasSize.width}
                height={canvasSize.height}
                onClick={handleStageClick}
                onTap={handleStageClick}
                onContextMenu={handleContextMenu}
                style={{ cursor: pendingTool ? 'crosshair' : 'default' }}
              >
              <Layer>
                <Rect x={0} y={0} width={canvasSize.width} height={canvasSize.height} fill="#ffffff" />
                
                {elements.map((el) => {
                  const isSelected = el.id === selectedId;
                  
                  // Common handlers for ResizableElement wrappers
                  // IMPORTANT: handleSelect only sets the selected element
                  // It does NOT open the properties panel - that's done via explicit action (floating toolbar, button click)
                  const handleSelect = () => { 
                    setSelectedId(el.id);
                    // Selection updates bounds via useEffect - no manual panel opening here
                  };
                  const handleDragEnd = (updatedEl) => {
                    updateElement(el.id, { x: updatedEl.x, y: updatedEl.y });
                    finishUpdate();
                    // Recalculate selection bounds immediately after drag
                    const newBounds = calculateElementBounds({ ...el, x: updatedEl.x, y: updatedEl.y });
                    setSelectionBounds(newBounds);
                  };
                  const handleTransform = (updatedEl) => {
                    // Update element with new transform values
                    updateElement(el.id, updatedEl);
                    finishUpdate();
                    // Recalculate selection bounds immediately after transform
                    const newBounds = calculateElementBounds({ ...el, ...updatedEl });
                    setSelectionBounds(newBounds);
                  };

                  // RECT with ResizableElement wrapper
                  if (el.type === ELEMENT_TYPES.RECT) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Rect
                          x={0}
                          y={0}
                          width={el.width}
                          height={el.height}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          cornerRadius={el.cornerRadius || 0}
                        />
                      </ResizableElement>
                    );
                  }

                  // CIRCLE with CircleResizable wrapper
                  if (el.type === ELEMENT_TYPES.CIRCLE) {
                    return (
                      <CircleResizable
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <KonvaCircle
                          x={0}
                          y={0}
                          radius={el.radius}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                        />
                      </CircleResizable>
                    );
                  }

                  // TEXT with TextResizable wrapper (horizontal resize only)
                  // Now includes reading aids options (syllables, silent letters, etc.)
                  if (el.type === ELEMENT_TYPES.TEXT) {
                    // Skip rendering if currently being edited (will show HTML input instead)
                    const isBeingEdited = editingTextId === el.id;
                    
                    // If reading aids are enabled, use PedagogicalTextKonva
                    if (el.showColoredSyllables || el.showSilentLetters || el.showLineAlternation) {
                      return (
                        <PedagogicalTextKonva
                          key={el.id}
                          element={el}
                          isSelected={isSelected}
                          onSelect={handleSelect}
                          onDragEnd={handleDragEnd}
                          onTransform={handleTransform}
                        />
                      );
                    }
                    
                    // Standard text rendering with advanced options
                    return (
                      <TextResizable
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                        onDoubleClick={handleTextDoubleClick}
                      >
                        <Text
                          x={0}
                          y={0}
                          text={isBeingEdited ? "" : el.text}
                          fontSize={el.fontSize}
                          fontFamily={el.fontFamily}
                          fontStyle={`${el.fontWeight === "bold" ? "bold " : ""}${el.fontStyle === "italic" ? "italic" : ""}`.trim() || "normal"}
                          textDecoration={el.textDecoration || ""}
                          fill={el.fill}
                          stroke={el.stroke || ""}
                          strokeWidth={el.strokeWidth || 0}
                          align={el.align || "left"}
                          width={el.width}
                          lineHeight={el.lineHeight || 1.4}
                          letterSpacing={el.letterSpacing || 0}
                          opacity={isBeingEdited ? 0 : 1}
                          perfectDrawEnabled={false}
                        />
                      </TextResizable>
                    );
                  }

                  // Legacy PEDAGOGICAL TEXT support (backwards compatibility)
                  if (el.type === "pedagogical_text") {
                    return (
                      <PedagogicalTextKonva
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      />
                    );
                  }

                  // IMAGE with ResizableElement wrapper
                  if (el.type === ELEMENT_TYPES.IMAGE && loadedImages[el.id]) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                        keepRatio={true}
                      >
                        <KonvaImage
                          x={0}
                          y={0}
                          image={loadedImages[el.id]}
                          width={el.width}
                          height={el.height}
                        />
                      </ResizableElement>
                    );
                  }

                  // Lignes Seyes (French writing lines)
                  if (el.type === ELEMENT_TYPES.SEYES) {
                    const lines = [];
                    const lineHeight = el.lineHeight || 8;
                    const numLines = Math.floor(el.height / lineHeight);
                    
                    // Background
                    lines.push(
                      <Rect
                        key={`${el.id}-bg`}
                        x={0}
                        y={0}
                        width={el.width}
                        height={el.height}
                        fill="#ffffff"
                        stroke="#e2e8f0"
                        strokeWidth={1}
                      />
                    );
                    
                    // Margin line (red)
                    if (el.showMargin) {
                      lines.push(
                        <Line
                          key={`${el.id}-margin`}
                          points={[24, 0, 24, el.height]}
                          stroke={el.marginColor || "#f87171"}
                          strokeWidth={1}
                        />
                      );
                    }
                    
                    // Horizontal lines
                    for (let i = 0; i <= numLines; i++) {
                      const y = i * lineHeight;
                      const isMainLine = i % 4 === 0;
                      lines.push(
                        <Line
                          key={`${el.id}-h-${i}`}
                          points={[0, y, el.width, y]}
                          stroke={el.lineColor || "#c4b5fd"}
                          strokeWidth={isMainLine ? 1.5 : 0.5}
                          opacity={isMainLine ? 1 : 0.6}
                        />
                      );
                    }
                    
                    // Text on lines - each line of text is positioned on a main Seyes line
                    if (el.text) {
                      const textLines = el.text.split('\n');
                      const fontSize = el.textFontSize || 20;
                      const startX = el.showMargin ? 28 : 4; // Start after margin if shown
                      
                      textLines.forEach((textLine, lineIdx) => {
                        // Position text on the main line (every 4th line in Seyes)
                        // The baseline should sit on the main line
                        const mainLineIndex = lineIdx * 4;
                        const yPos = mainLineIndex * lineHeight;
                        
                        // Only render if within bounds
                        if (yPos < el.height - fontSize) {
                          lines.push(
                            <Text
                              key={`${el.id}-text-${lineIdx}`}
                              x={startX}
                              y={yPos + 2} // Slight offset to sit on the line
                              text={textLine}
                              fontSize={fontSize}
                              fontFamily={el.textFontFamily || "Cursive"}
                              fill={el.textColor || "#1e293b"}
                              width={el.width - startX - 4}
                            />
                          );
                        }
                      });
                    }
                    
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Group>{lines}</Group>
                      </ResizableElement>
                    );
                  }

                  // Quadrillage (Grid)
                  if (el.type === ELEMENT_TYPES.GRID) {
                    const gridLines = [];
                    const cellSize = el.cellSize || 20;
                    const cols = Math.floor(el.width / cellSize);
                    const rows = Math.floor(el.height / cellSize);
                    
                    // Background
                    gridLines.push(
                      <Rect
                        key={`${el.id}-bg`}
                        x={0}
                        y={0}
                        width={el.width}
                        height={el.height}
                        fill="#ffffff"
                        stroke="#e2e8f0"
                        strokeWidth={1}
                      />
                    );
                    
                    if (el.gridType === "dots") {
                      // Dots grid
                      for (let i = 0; i <= cols; i++) {
                        for (let j = 0; j <= rows; j++) {
                          gridLines.push(
                            <KonvaCircle
                              key={`${el.id}-dot-${i}-${j}`}
                              x={i * cellSize}
                              y={j * cellSize}
                              radius={1.5}
                              fill={el.strokeColor || "#94a3b8"}
                            />
                          );
                        }
                      }
                    } else {
                      // Square grid (default)
                      for (let i = 0; i <= cols; i++) {
                        gridLines.push(
                          <Line
                            key={`${el.id}-v-${i}`}
                            points={[i * cellSize, 0, i * cellSize, el.height]}
                            stroke={el.strokeColor || "#94a3b8"}
                            strokeWidth={el.strokeWidth || 1}
                          />
                        );
                      }
                      for (let j = 0; j <= rows; j++) {
                        gridLines.push(
                          <Line
                            key={`${el.id}-h-${j}`}
                            points={[0, j * cellSize, el.width, j * cellSize]}
                            stroke={el.strokeColor || "#94a3b8"}
                            strokeWidth={el.strokeWidth || 1}
                          />
                        );
                      }
                    }
                    
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Group>{gridLines}</Group>
                      </ResizableElement>
                    );
                  }

                  // Tableau (Table)
                  if (el.type === ELEMENT_TYPES.TABLE) {
                    const tableElements = [];
                    const rows = el.rows || 3;
                    const cols = el.cols || 3;
                    const cellWidth = el.width / cols;
                    const cellHeight = el.height / rows;
                    
                    // Background
                    tableElements.push(
                      <Rect
                        key={`${el.id}-bg`}
                        x={0}
                        y={0}
                        width={el.width}
                        height={el.height}
                        fill={el.fillColor || "#ffffff"}
                        stroke={el.strokeColor || "#1e293b"}
                        strokeWidth={el.strokeWidth || 2}
                      />
                    );
                    
                    // Header row
                    if (el.headerColor) {
                      tableElements.push(
                        <Rect
                          key={`${el.id}-header`}
                          x={0}
                          y={0}
                          width={el.width}
                          height={cellHeight}
                          fill={el.headerColor}
                        />
                      );
                    }
                    
                    // Vertical lines
                    for (let i = 1; i < cols; i++) {
                      tableElements.push(
                        <Line
                          key={`${el.id}-v-${i}`}
                          points={[i * cellWidth, 0, i * cellWidth, el.height]}
                          stroke={el.strokeColor || "#1e293b"}
                          strokeWidth={el.strokeWidth || 2}
                        />
                      );
                    }
                    
                    // Horizontal lines
                    for (let j = 1; j < rows; j++) {
                      tableElements.push(
                        <Line
                          key={`${el.id}-h-${j}`}
                          points={[0, j * cellHeight, el.width, j * cellHeight]}
                          stroke={el.strokeColor || "#1e293b"}
                          strokeWidth={el.strokeWidth || 2}
                        />
                      );
                    }
                    
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Group>{tableElements}</Group>
                      </ResizableElement>
                    );
                  }

                  // QR Code - uses QRCodeKonvaElement for real QR generation
                  if (el.type === ELEMENT_TYPES.QRCODE) {
                    return (
                      <QRCodeKonvaElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      />
                    );
                  }

                  // Audio element (TTS or Recording)
                  if (el.type === ELEMENT_TYPES.AUDIO) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Group>
                          <Rect
                            x={0}
                            y={0}
                            width={el.width}
                            height={el.height}
                            fill={el.bgColor || "#8b5cf6"}
                            stroke="#7c3aed"
                            strokeWidth={2}
                            cornerRadius={12}
                          />
                          {/* Speaker icon area */}
                          <KonvaCircle
                            x={35}
                            y={el.height / 2}
                            radius={18}
                            fill="#ffffff"
                            opacity={0.9}
                          />
                          <Text
                            x={22}
                            y={el.height / 2 - 10}
                            text={el.audioType === "recording" ? "🎤" : "🔊"}
                            fontSize={18}
                          />
                          {/* Text preview */}
                          <Text
                            x={65}
                            y={el.height / 2 - 10}
                            text={el.text || "Audio"}
                            fontSize={14}
                            fill="#ffffff"
                            width={el.width - 80}
                            fontFamily="Nunito"
                            fontStyle="bold"
                            ellipsis={true}
                            wrap="none"
                          />
                          <Text
                            x={65}
                            y={el.height / 2 + 8}
                            text={el.audioType === "recording" ? "Enregistrement" : "Text-to-Speech"}
                            fontSize={10}
                            fill="rgba(255,255,255,0.7)"
                          />
                        </Group>
                      </ResizableElement>
                    );
                  }

                  // QCM Interactive
                  if (el.type === ELEMENT_TYPES.QCM_INTERACTIVE) {
                    const options = el.options || ["A", "B", "C"];
                    const optionHeight = 35;
                    const questionHeight = 50;
                    
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Group>
                          {/* Background */}
                          <Rect
                            x={0}
                            y={0}
                            width={el.width}
                            height={el.height}
                            fill="#ffffff"
                            stroke="#e2e8f0"
                            strokeWidth={2}
                            cornerRadius={12}
                          />
                          {/* Question area */}
                          <Rect
                            x={8}
                            y={8}
                            width={el.width - 16}
                            height={questionHeight}
                            fill="#f1f5f9"
                            cornerRadius={8}
                          />
                          <Text
                            x={16}
                            y={20}
                            text={el.question || "Question ?"}
                            fontSize={16}
                            fill="#1e293b"
                            fontFamily="Fredoka"
                            fontStyle="bold"
                            width={el.width - 32}
                          />
                          {/* Options */}
                          {options.map((opt, idx) => (
                            <Group key={idx}>
                              <Rect
                                x={12}
                                y={questionHeight + 20 + idx * (optionHeight + 8)}
                                width={el.width - 24}
                                height={optionHeight}
                                fill={idx === el.correctIndex ? "#dcfce7" : "#f8fafc"}
                                stroke={idx === el.correctIndex ? "#22c55e" : "#e2e8f0"}
                                strokeWidth={2}
                                cornerRadius={8}
                              />
                              <Text
                                x={45}
                                y={questionHeight + 28 + idx * (optionHeight + 8)}
                                text={opt}
                                fontSize={14}
                                fill={idx === el.correctIndex ? "#166534" : "#475569"}
                                fontFamily="Nunito"
                              />
                              <KonvaCircle
                                x={28}
                                y={questionHeight + 37 + idx * (optionHeight + 8)}
                                radius={10}
                                fill={idx === el.correctIndex ? "#22c55e" : "#ffffff"}
                                stroke={idx === el.correctIndex ? "#16a34a" : "#cbd5e1"}
                                strokeWidth={2}
                              />
                            </Group>
                          ))}
                          {/* Interactive badge */}
                          <Text
                            x={el.width - 85}
                            y={el.height - 20}
                            text="🎯 Interactif"
                            fontSize={10}
                            fill="#6366f1"
                          />
                        </Group>
                      </ResizableElement>
                    );
                  }

                  // Phase B: Highlighter
                  if (el.type === ELEMENT_TYPES.HIGHLIGHTER) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Rect
                          x={0}
                          y={0}
                          width={el.width}
                          height={el.height}
                          fill={el.fill || "#fef08a"}
                          opacity={el.opacity || 0.6}
                          cornerRadius={el.cornerRadius || 4}
                        />
                      </ResizableElement>
                    );
                  }

                  // Phase B: Triangle
                  if (el.type === ELEMENT_TYPES.TRIANGLE) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                        keepRatio={false}
                      >
                        <RegularPolygon
                          x={el.width / 2}
                          y={el.height / 2}
                          sides={3}
                          radius={Math.min(el.width, el.height) / 2}
                          fill={el.fill || "#3b82f6"}
                          stroke={el.stroke || "#1e40af"}
                          strokeWidth={el.strokeWidth || 2}
                          rotation={0}
                        />
                      </ResizableElement>
                    );
                  }

                  // Phase B: Star
                  if (el.type === ELEMENT_TYPES.STAR) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                        keepRatio={true}
                      >
                        <KonvaStar
                          x={el.width / 2}
                          y={el.height / 2}
                          numPoints={el.numPoints || 5}
                          innerRadius={Math.min(el.width, el.height) / 5}
                          outerRadius={Math.min(el.width, el.height) / 2}
                          fill={el.fill || "#eab308"}
                          stroke={el.stroke || "#a16207"}
                          strokeWidth={el.strokeWidth || 2}
                        />
                      </ResizableElement>
                    );
                  }

                  // Phase B: Arrow
                  if (el.type === ELEMENT_TYPES.ARROW) {
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <KonvaArrow
                          points={[0, el.height / 2, el.width, el.height / 2]}
                          pointerLength={el.pointerLength || 15}
                          pointerWidth={el.pointerWidth || 12}
                          fill={el.fill || "#1e293b"}
                          stroke={el.stroke || "#1e293b"}
                          strokeWidth={el.strokeWidth || 3}
                        />
                      </ResizableElement>
                    );
                  }

                  // Phase B: Freedraw (line-based drawing)
                  if (el.type === ELEMENT_TYPES.FREEDRAW && el.points && el.points.length > 0) {
                    // Convert points array to flat array for Konva Line
                    const flatPoints = el.points.flatMap(p => [p.x, p.y]);
                    return (
                      <ResizableElement
                        key={el.id}
                        element={el}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                        onDragEnd={handleDragEnd}
                        onTransform={handleTransform}
                      >
                        <Line
                          points={flatPoints}
                          stroke={el.stroke || "#1e293b"}
                          strokeWidth={el.strokeWidth || 3}
                          tension={el.tension || 0.5}
                          lineCap={el.lineCap || "round"}
                          lineJoin={el.lineJoin || "round"}
                        />
                      </ResizableElement>
                    );
                  }

                  return null;
                })}
              </Layer>
            </Stage>
            
            {/* Floating Toolbar - appears near selected element */}
            {selectedElement && selectionBounds && !selectedElement.locked && !editingTextId && (
              <FloatingToolbar
                element={selectedElement}
                position={selectionBounds}
                onDelete={deleteElement}
                onDuplicate={() => duplicateElement()}
                onUpdate={updateElement}
                onBringForward={bringForward}
                onSendBackward={sendBackward}
                onBringToFront={() => {
                  // Bring to very front
                  const index = elements.findIndex(el => el.id === selectedId);
                  if (index < elements.length - 1) {
                    const newElements = elements.filter(el => el.id !== selectedId);
                    newElements.push(selectedElement);
                    setElements(newElements);
                    addToHistory(newElements);
                  }
                }}
                onSendToBack={() => {
                  // Send to very back
                  const index = elements.findIndex(el => el.id === selectedId);
                  if (index > 0) {
                    const newElements = elements.filter(el => el.id !== selectedId);
                    newElements.unshift(selectedElement);
                    setElements(newElements);
                    addToHistory(newElements);
                  }
                }}
                canvasScale={canvasScale}
              />
            )}

            {/* Inline text editing overlay */}
            {editingTextId && (() => {
              const editingElement = elements.find(el => el.id === editingTextId);
              if (!editingElement) return null;
              
              // Calculate position based on element's position on canvas
              const textareaStyle = {
                position: "absolute",
                top: editingElement.y,
                left: editingElement.x,
                width: editingElement.width || 200,
                minHeight: 40,
                fontSize: editingElement.fontSize || 16,
                fontFamily: editingElement.fontFamily || "Arial",
                fontWeight: editingElement.fontWeight || "normal",
                fontStyle: editingElement.fontStyle || "normal",
                color: editingElement.fill || "#000000",
                textAlign: editingElement.align || "left",
                lineHeight: editingElement.lineHeight || 1.4,
                letterSpacing: editingElement.letterSpacing || 0,
                border: "2px solid #3b82f6",
                borderRadius: "4px",
                padding: "4px 8px",
                background: "rgba(255, 255, 255, 0.95)",
                outline: "none",
                resize: "none",
                boxShadow: "0 4px 20px rgba(59, 130, 246, 0.3)",
                zIndex: 1000,
              };
              
              return (
                <textarea
                  ref={textInputRef}
                  data-testid="inline-text-editor"
                  style={textareaStyle}
                  value={editingTextValue}
                  onChange={(e) => setEditingTextValue(e.target.value)}
                  onBlur={finishTextEditing}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      finishTextEditing();
                    }
                    if (e.key === "Escape") {
                      cancelTextEditing();
                    }
                  }}
                  placeholder="Saisir le texte..."
                />
              );
            })()}
          </div>
        </div>
      </div>

        {/* Desktop right panel with tabs */}
        <div className="hidden md:flex w-72 bg-white border-l border-slate-200 flex-col flex-shrink-0">
          {/* Tab buttons */}
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setRightSidebarTab("properties")}
              className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                rightSidebarTab === "properties"
                  ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Settings2 size={16} />
              Propriétés
            </button>
            <button
              onClick={() => setRightSidebarTab("layers")}
              className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                rightSidebarTab === "layers"
                  ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Layers size={16} />
              Calques
            </button>
          </div>
          
          {/* Tab content */}
          {rightSidebarTab === "properties" ? (
            <div className="flex-1 p-4 overflow-y-auto">
              <PropertiesContent
                selectedElement={selectedElement}
                onUpdate={updateElement}
                onDelete={deleteElement}
              />
            </div>
          ) : (
            <LayersPanel
              elements={elements}
              selectedId={selectedId}
              onSelectElement={(id) => { setSelectedId(id); setRightSidebarTab("properties"); }}
              onReorderElements={(newElements) => { setElements(newElements); addToHistory(newElements); }}
              onUpdateElement={updateElement}
              onDeleteElement={deleteElement}
              onDuplicateElement={() => duplicateElement()}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
            />
          )}
        </div>
      </div>

      {/* Mobile bottom toolbar */}
      <div className="md:hidden h-16 bg-white border-t border-slate-200 flex items-center justify-around px-2 flex-shrink-0">
        <button
          onClick={() => setShowTools(true)}
          className="flex flex-col items-center gap-1 p-2"
        >
          <Plus size={24} className="text-blue-500" />
          <span className="text-xs font-bold text-slate-600">Ajouter</span>
        </button>
        <button
          onClick={() => setShowTemplates(true)}
          className="flex flex-col items-center gap-1 p-2"
        >
          <Layers size={24} className="text-orange-500" />
          <span className="text-xs font-bold text-slate-600">Modeles</span>
        </button>
        <button
          onClick={() => setShowAI(true)}
          className="flex flex-col items-center gap-1 p-2"
        >
          <Sparkles size={24} className="text-purple-500" />
          <span className="text-xs font-bold text-slate-600">IA</span>
        </button>
        <button
          onClick={() => { if (selectedId) setShowProperties(true); }}
          disabled={!selectedId}
          className="flex flex-col items-center gap-1 p-2 disabled:opacity-30"
        >
          <Settings2 size={24} className="text-slate-600" />
          <span className="text-xs font-bold text-slate-600">Editer</span>
        </button>
      </div>

      {/* Mobile Bottom Sheets */}
      <BottomSheet isOpen={showTools} onClose={() => setShowTools(false)} title="Ajouter un element">
        <div className="space-y-4">
          {/* Basic shapes */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Formes de base</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { type: ELEMENT_TYPES.TEXT, icon: Type, label: "Texte", color: "blue" },
                { type: ELEMENT_TYPES.RECT, icon: Square, label: "Rectangle", color: "green" },
                { type: ELEMENT_TYPES.CIRCLE, icon: Circle, label: "Cercle", color: "orange" },
              ].map((tool) => (
                <button
                  key={tool.type}
                  onClick={() => addElement(tool.type)}
                  className={`flex items-center gap-3 p-3 bg-${tool.color}-50 hover:bg-${tool.color}-100 rounded-xl border-2 border-${tool.color}-200`}
                >
                  <tool.icon size={24} className={`text-${tool.color}-500`} />
                  <span className="font-bold text-sm text-slate-700">{tool.label}</span>
                </button>
              ))}
              <button
                onClick={() => { setShowTools(false); setShowClipArtLibrary(true); }}
                className="flex items-center gap-3 p-3 bg-pink-50 hover:bg-pink-100 rounded-xl border-2 border-pink-200"
              >
                <Image size={24} className="text-pink-500" />
                <span className="font-bold text-sm text-slate-700">Bibliotheque</span>
              </button>
            </div>
          </div>
          
          {/* Educational elements */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Outils educatifs</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => addElement(ELEMENT_TYPES.SEYES)}
                className="flex items-center gap-3 p-3 bg-violet-50 hover:bg-violet-100 rounded-xl border-2 border-violet-200"
              >
                <GripVertical size={24} className="text-violet-500" />
                <span className="font-bold text-sm text-slate-700">Lignes Seyes</span>
              </button>
              <button
                onClick={() => addElement(ELEMENT_TYPES.GRID)}
                className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border-2 border-slate-300"
              >
                <Grid3X3 size={24} className="text-slate-600" />
                <span className="font-bold text-sm text-slate-700">Quadrillage</span>
              </button>
              <button
                onClick={() => addElement(ELEMENT_TYPES.TABLE)}
                className="flex items-center gap-3 p-3 bg-cyan-50 hover:bg-cyan-100 rounded-xl border-2 border-cyan-200"
              >
                <Table size={24} className="text-cyan-600" />
                <span className="font-bold text-sm text-slate-700">Tableau</span>
              </button>
              <button
                onClick={() => addElement(ELEMENT_TYPES.QRCODE)}
                className="flex items-center gap-3 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl border-2 border-emerald-200"
              >
                <QrCode size={24} className="text-emerald-600" />
                <span className="font-bold text-sm text-slate-700">QR Code</span>
              </button>
            </div>
          </div>
          
          {/* Interactive elements */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Elements interactifs</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => addElement(ELEMENT_TYPES.AUDIO)}
                className="flex items-center gap-3 p-3 bg-purple-50 hover:bg-purple-100 rounded-xl border-2 border-purple-200"
              >
                <Volume2 size={24} className="text-purple-500" />
                <span className="font-bold text-sm text-slate-700">Audio</span>
              </button>
              <button
                onClick={() => addElement(ELEMENT_TYPES.QCM_INTERACTIVE)}
                className="flex items-center gap-3 p-3 bg-indigo-50 hover:bg-indigo-100 rounded-xl border-2 border-indigo-200"
              >
                <CheckCircle size={24} className="text-indigo-500" />
                <span className="font-bold text-sm text-slate-700">QCM Interactif</span>
              </button>
            </div>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showTemplates} onClose={() => setShowTemplates(false)} title="Modeles">
        <div className="grid grid-cols-3 gap-3">
          {TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => applyTemplate(template)}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 rounded-2xl border-2 border-slate-200"
            >
              <span className="text-3xl">{template.icon}</span>
              <span className="font-bold text-sm text-slate-700">{template.name}</span>
            </button>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showCliparts} onClose={() => setShowCliparts(false)} title="Cliparts">
        <div className="grid grid-cols-4 gap-3">
          {CLIPARTS.map((clipart) => (
            <button
              key={clipart.id}
              onClick={() => addClipart(clipart)}
              className="p-3 bg-slate-50 hover:bg-pink-50 rounded-xl border-2 border-slate-200 hover:border-pink-300"
            >
              <img src={clipart.url} alt={clipart.id} className="w-12 h-12 mx-auto" />
            </button>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showAI} onClose={() => setShowAI(false)} title="Assistant IA Navi">
        <AIAssistantPanel 
          onGenerateText={addAIText} 
          onClose={() => setShowAI(false)}
          currentElements={elements}
          documentTitle={documentTitle}
          onGenerateStructure={(structure) => {
            // Handle structure generation - add multiple elements based on structure
            console.log("Structure selected:", structure);
            toast.info(`Structure "${structure.name}" - Fonctionnalité à venir`);
          }}
        />
      </BottomSheet>

      <BottomSheet isOpen={showProperties && !!selectedId} onClose={() => setShowProperties(false)} title="Proprietes">
        <PropertiesContent
          selectedElement={selectedElement}
          onUpdate={updateElement}
          onDelete={deleteElement}
        />
      </BottomSheet>

      {/* Page Settings Dialog */}
      <PageSettingsDialog
        open={showPageSettings}
        onOpenChange={setShowPageSettings}
        currentFormat={pageFormat}
        currentOrientation={pageOrientation}
        onApply={handlePageSettingsApply}
      />

      {/* Export Dialog */}
      <ExportOutputDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        resourceId={documentId}
        documentTitle={documentTitle}
        onExportPNG={exportImage}
        onExportPDF={exportPDF}
        onExportGrouped={handleExportGrouped}
        stageRef={stageRef}
      />

      {/* Target Assignment Panel */}
      <TargetAssignmentPanel
        open={showTargetPanel}
        onOpenChange={setShowTargetPanel}
        currentTarget={targetConfig}
        onTargetChange={handleTargetChange}
        onGenerateVariants={handleGenerateVariants}
      />

      {/* Export Preview Dialog - Fullscreen */}
      <ExportPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        pages={pages}
        currentPageIndex={currentPageIndex}
        stageRef={stageRef}
        documentTitle={documentTitle}
        pageFormat={pageFormat}
        pageOrientation={pageOrientation}
        onExportPDF={exportPDF}
        onExportPNG={exportImage}
        onExportGrouped={handleExportGrouped}
        onPrint={handlePrint}
      />

      {/* ClipArt Library Dialog */}
      {/* Phase D: Advanced Tools Dialog */}
      <AdvancedToolsDialog
        open={showAdvancedTools}
        onClose={() => setShowAdvancedTools(false)}
        onAddWatermark={addWatermark}
        onAddStamp={addStamp}
        onAddPageNumbers={addPageNumbers}
        onApplyNup={applyNup}
        onAddOverlay={addOverlay}
        pages={pages}
        currentPageIndex={currentPageIndex}
      />

      {/* Phase D: PDF Composer Dialog */}
      <PDFComposerDialog
        open={showPDFComposer}
        onClose={() => setShowPDFComposer(false)}
        onInsertCrop={insertPDFCrop}
      />

      <ClipArtLibraryDialog
        open={showClipArtLibrary}
        onOpenChange={setShowClipArtLibrary}
        onSelectClipArt={addClipart}
        onSelectBlock={addPedagogicBlock}
      />

      {/* Context Menu */}
      {contextMenuPos && (
        <ElementContextMenu
          position={contextMenuPos}
          element={selectedElement}
          onClose={() => setContextMenuPos(null)}
          onCopy={copyElement}
          onCut={cutElement}
          onPaste={pasteElement}
          onDelete={() => selectedElement && deleteElement(selectedElement.id)}
          onDuplicate={duplicateElement}
          onFitToWidth={fitToPageWidth}
          onBringForward={bringForward}
          onSendBackward={sendBackward}
          onUpdateElement={(prop, value) => selectedElement && updateElement(selectedElement.id, { [prop]: value })}
          canvasWidth={canvasSize.width}
        />
      )}

      {/* Resource Metadata Sheet */}
      <ResourceMetadataSheet
        open={showMetadataSheet}
        onOpenChange={setShowMetadataSheet}
        resourceId={documentId}
        documentTitle={documentTitle}
        elements={elements}
        pages={pages}
        pageSettings={{ format: pageFormat, orientation: pageOrientation, width: canvasSize.width, height: canvasSize.height }}
        onSaved={handleResourceSaved}
        initialData={resourceMetadata}
      />
    </div>
  );
}
